// Libraries
import React from 'react'
import Marketo from '../../../global/marketo'
import Layout from '../../../layouts/layout'

// Webinar Page Modules
import SEOTags from '../../../global/seoTags'
import FormPageHero from '../formPageHero'

// Helpers
import ImgUtil from '../../../../utils/img.utils'
import {
  correctSlugPrefix,
  generateThankYouPageURL,
  isExternalURL,
} from '../../../../utils/link.utils'
import { findTarget } from '../../../../utils/microcopy.utils'
import { renderRichText } from '../../../../utils/richTextHandler.utils'

// Constants
import { LocaleCodes } from '../../../../constants/localization.constants'

// Styles
import { BodyContent, BodyOuter, MarketoFormContainer } from '../formPage'

import WrapperImage from '../../../global/wrapperImage'
import {
  RobotReleaseContainer,
  WebinarFinePrint,
  WebinarFormIntro,
  WebinarSpeakersTitle,
} from './style'

const WebinarPage = ({
  pageContext,
  location,
  path,
  headerData,
  footerData,
  announcementData,
  browserLanguageData,
}: any) => {
  let hero: any
  let pageData = pageContext.webinarData ? pageContext.webinarData : ''

  if (pageData && pageData.h1Title) {
    let webinarHeroType = ''
    if (pageData.heroTextCentered && pageData.greyTextBar) {
      webinarHeroType = 'GRAY_TEXT_BAR'
    }

    if (pageData.heroTextCentered && !pageData.greyTextBar) {
      webinarHeroType = 'CENTERED_NO_GRAY'
    }

    if (!pageData.heroTextCentered) {
      webinarHeroType = 'LEFT_ALIGNED'
    }

    let heroProps = {
      h1Title: pageData.h1Title ? pageData.h1Title : '',
      image: {
        imageSrc:
          pageData.mainImage &&
          pageData.mainImage.image &&
          pageData.mainImage.image.file &&
          pageData.mainImage.image.file.url
            ? pageData.mainImage.image.file.url
            : '/',
        alt: pageData.mainImage && pageData.mainImage.altText ? pageData.mainImage.altText : '',
      },
      heroSubtitle: pageData.heroSubtitle,
      heroTextCentered: pageData.heroTextCentered,
      heroEyebrow: pageData.heroEyebrow,
      greyTextBar: pageData.greyTextBar,
      webinarHeroType,
      isWebinar: true,
    }

    hero = <FormPageHero {...heroProps} />
  }

  const defaultSlug = pageData && pageData.slug ? pageData.slug : '/'
  const seoData = pageData && pageData.seoMetadata ? pageData.seoMetadata : null

  let defaultLocale: string
  if (pageContext && pageContext.pageLocale) {
    defaultLocale = pageContext.pageLocale
  } else {
    defaultLocale = LocaleCodes.ENGLISH.code
  }

  const genericTYPageURL = '/g/thank-you-webinar'

  const thankYouPageUrl = generateThankYouPageURL(genericTYPageURL, pageData.thankYouPage)

  const marketoFormData = pageContext.marketoFormData

  let marketoJsScript = null

  if (pageData.marketoJsScript && pageData.marketoJsScript.marketoJsScript) {
    marketoJsScript = pageData.marketoJsScript.marketoJsScript
  }

  let speakerText = null
  const countryCode = defaultLocale.slice(3)
  const number = pageData?.resourceCategory?.number
  const englishSpeakerText = `resource.${number}.speakers.text`
  const globalSpeakerText = `resource.${countryCode}.${number}.speakers.text`

  if (pageData.microcopy && pageData.microcopy.values) {
    speakerText = findTarget(
      defaultLocale === LocaleCodes.ENGLISH.code ? englishSpeakerText : globalSpeakerText,
      pageData.microcopy.values
    )
  }

  const formSuccessHandler = (form: any) => {
    if (window && window.hasOwnProperty('MktoForms2')) {
      form.onSuccess(() => {
        window.location.href = correctSlugPrefix(thankYouPageUrl)
        return false
      })
    }
  }

  const renderRobotRelease = () => {
    if (pageData.robotRelease) {
      if (pageData.robotRelease.image?.file?.url) {
        const robotReleaseImg = (
          <WrapperImage
            src={ImgUtil.getResizedImageUrl(pageData.robotRelease.image?.file?.url, ``)}
            alt={pageData.robotRelease.image?.imageAlt}
            className="RobotRelease__Img-File"
            width={200}
            height={140}
            threshold={650}
          />
        )
        if (pageData.robotReleaseLink) {
          let target
          if (isExternalURL(pageData.robotReleaseLink)) {
            target = '_blank'
          } else {
            target = '_self'
          }
          return (
            <RobotReleaseContainer>
              <a href={pageData.robotReleaseLink} target={target}>
                {robotReleaseImg}
              </a>
            </RobotReleaseContainer>
          )
        } else {
          return <RobotReleaseContainer>{robotReleaseImg}</RobotReleaseContainer>
        }
      }
    }
  }

  let useCase = pageData?.useCase

  const getFormIntroText = () => {
    let formIntroText = ''
    if (pageData.formIntro) {
      formIntroText = pageData.formIntro
    } else {
      if (
        pageData.microcopy &&
        pageData.resourceCategory &&
        pageData.resourceCategory.number &&
        (useCase === 'Upcoming' || useCase === 'On Demand')
      ) {
        let type = useCase === 'Upcoming' ? 'UpcomingFormINTRO' : 'OnDemandFormINTRO'
        const englishIntroText = `resource.${number}.${type}.text`
        const globalIntroText = `resource.${countryCode}.${number}.${type}.text`

        formIntroText = findTarget(
          defaultLocale === LocaleCodes.ENGLISH.code ? englishIntroText : globalIntroText,
          pageData.microcopy.values
        )
      }
    }
    return formIntroText
  }

  const pageInner = (
    <React.Fragment>
      <SEOTags
        seoMetadata={seoData}
        path={path}
        slug={defaultSlug}
        locale={defaultLocale}
        availableLocales={pageContext.availableLocales}
      />
      {hero}
      <BodyOuter formAlignToBody={true}>
        <div>
          <BodyContent
            className="AR-WP__Body"
            isRobotRelease={pageData?.robotRelease?.image?.file?.url}
            isWebinar={true}>
            {renderRobotRelease()}
            <WebinarSpeakersTitle className="WebinarSpeakers__title">
              {speakerText}
            </WebinarSpeakersTitle>
            {pageData.body && renderRichText(pageData.body)}
            <WebinarFinePrint className="Webinar__Fine-Print">
              {pageData.finePrint && renderRichText(pageData.finePrint)}
            </WebinarFinePrint>
          </BodyContent>
          <MarketoFormContainer formAlignToBody={true}>
            <div>
              <WebinarFormIntro>
                <p className="WebinarFormIntro">{getFormIntroText()}</p>
              </WebinarFormIntro>
              <Marketo
                formData={marketoFormData && marketoFormData.marketoForm}
                formCallbacks={formSuccessHandler}
                marketoJsScript={marketoJsScript}
              />
            </div>
          </MarketoFormContainer>
        </div>
      </BodyOuter>
    </React.Fragment>
  )

  return (
    <Layout.Template
      lang={defaultLocale}
      minimalFooter={true}
      minimalHeader={true}
      headerData={headerData}
      footerData={footerData}
      announcementData={announcementData}
      browserLanguageData={browserLanguageData}
      pageType="webinar"
      slug={defaultSlug}
      availableLocales={pageContext.availableLocales}>
      <div data-cy="Webinar">{pageInner}</div>
    </Layout.Template>
  )
}

export default WebinarPage
